// CoinContext.jsx — نسخه‌ی نهایی، اتمی، چند-کاربره و بین-تب
// ================================================================
//  اصول طراحی:
//   • استفاده از useReducer برای به‌روزرسانی اتمی coins و xp
//   • همگام‌سازی خودکار با localStorage مخصوص هر کاربر
//   • همگام‌سازی بین تب‌های مرورگر (storage event)
//   • جلوگیری از setState روی کاربر مهمان (guest)
//   • حفظ کامل API قبلی + افزودن چند helper مفید
// ================================================================

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useReducer,
} from "react";
import { useAuth } from "./AuthContext";
import { getToken } from '../utils/secureStorage';
// ================================================================
//  ایجاد Context
// ================================================================
const CoinContext = createContext(null);

// ================================================================
//  توابع کمکی
// ================================================================

/** تبدیل امن هر مقدار به عدد صحیح نامنفی */
const toNonNegInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
};

// ============================================================
//  توابع کمکی برای کار با سرور (به‌جای localStorage)
// ============================================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** خواندن سکه و امتیاز کاربر از سرور (userRewards) */
const fetchRewardsFromServer = async (userId) => {
  if (!userId || userId === 'guest') return { coins: 0, xp: 0, id: null };
  try {
    const token = getToken();
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    const res = await fetch(`${API_BASE}/userRewards?userId=${encodeURIComponent(userId)}`, {
      headers: authHeaders
    });
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      return {
        coins: toNonNegInt(data[0].coins),
        xp: toNonNegInt(data[0].xp),
        id: data[0].id,
      };
    }
    // اگر رکوردی نبود، یکی بساز
    const createRes = await fetch(`${API_BASE}/userRewards`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({ userId, coins: 0, xp: 0 }),
    });
    const created = await createRes.json();
    return { coins: 0, xp: 0, id: created.id };
  } catch (e) {
    console.warn('⚠️ CoinContext fetchRewards failed:', e);
    return { coins: 0, xp: 0, id: null };
  }
};

/** ذخیره سکه و امتیاز کاربر در سرور (userRewards) */
const saveRewardsToServer = async (userId, coins, xp, rewardId) => {
  if (!userId || userId === 'guest') return;
  const safeCoins = toNonNegInt(coins);
  const safeXp = toNonNegInt(xp);
  try {
    const token = getToken();
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    if (rewardId) {
      await fetch(`${API_BASE}/userRewards/${rewardId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ coins: safeCoins, xp: safeXp }),
      });
      return;
    }
    // اگر rewardId نداشتیم، اول query کن
    const res = await fetch(`${API_BASE}/userRewards?userId=${encodeURIComponent(userId)}`, {
      headers: authHeaders
    });
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      await fetch(`${API_BASE}/userRewards/${data[0].id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ coins: safeCoins, xp: safeXp }),
      });
    } else {
      await fetch(`${API_BASE}/userRewards`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ userId, coins: safeCoins, xp: safeXp }),
      });
    }
  } catch (e) {
    console.warn('⚠️ CoinContext saveRewards failed:', e);
  }
};
// ================================================================
//  Reducer — به‌روزرسانی اتمی coins و xp
// ================================================================
const initialState = { coins: 0, xp: 0 };

function rewardsReducer(state, action) {
  switch (action.type) {
    // ------------------------------------------------------------
    //  بارگذاری مقدار (از localStorage یا سرور یا تب دیگر)
    // ------------------------------------------------------------
    case "HYDRATE": {
      const c = toNonNegInt(action.coins);
      const x = toNonNegInt(action.xp);
      if (state.coins === c && state.xp === x) return state;
      return { coins: c, xp: x };
    }

    // ------------------------------------------------------------
    //  افزودن
    // ------------------------------------------------------------
    case "ADD_COINS": {
      const d = toNonNegInt(action.amount);
      if (d === 0) return state;
      return { ...state, coins: state.coins + d };
    }
    case "ADD_XP": {
      const d = toNonNegInt(action.amount);
      if (d === 0) return state;
      return { ...state, xp: state.xp + d };
    }

    // ------------------------------------------------------------
    //  کاهش (کف صفر)
    // ------------------------------------------------------------
    case "REMOVE_COINS": {
      const d = toNonNegInt(action.amount);
      if (d === 0) return state;
      const next = Math.max(0, state.coins - d);
      if (next === state.coins) return state;
      return { ...state, coins: next };
    }
    case "REMOVE_XP": {
      const d = toNonNegInt(action.amount);
      if (d === 0) return state;
      const next = Math.max(0, state.xp - d);
      if (next === state.xp) return state;
      return { ...state, xp: next };
    }

    // ------------------------------------------------------------
    //  جایگزینی کامل
    // ------------------------------------------------------------
    case "SET": {
      const c = toNonNegInt(action.coins);
      const x = toNonNegInt(action.xp);
      if (state.coins === c && state.xp === x) return state;
      return { coins: c, xp: x };
    }

    // ------------------------------------------------------------
    //  صفر کردن
    // ------------------------------------------------------------
    case "CLEAR": {
      if (state.coins === 0 && state.xp === 0) return state;
      return { coins: 0, xp: 0 };
    }

    default:
      return state;
  }
}

// ================================================================
//  Provider
// ================================================================
export function CoinProvider({ children }) {
  const { user } = useAuth();
const userId = user?.id || "guest";

// ============================================================
//  state اصلی (اتمی) — با مقادیر پیش‌فرض (از سرور پر می‌شود)
// ============================================================
const [state, dispatch] = useReducer(rewardsReducer, initialState);

// ✅ id رکورد userRewards در سرور (برای PATCH سریع)
const rewardIdRef = useRef(null);

// ✅ ردیابی آخرین کاربری که state برایش hydrate شده
// (برای جلوگیری از ذخیره مقدار اولیه صفر روی سرور قبل از بارگذاری)
const hydratedUserRef = useRef(null);

  // ============================================================
  //  state انیمیشن (id یکتا برای پشتیبانی از re-trigger)
  // ============================================================
  const [animation, setAnimation] = useState(null);
  const animationTimerRef = useRef(null);

  // ============================================================
  //  cooldown — برای جلوگیری از spam در کلیک‌های تکراری
  // ============================================================
  const lastActionTimeRef = useRef(0);
  const COOLDOWN_MS = 250;

  // ============================================================
  //  رفرنس همیشه-به‌روز از userId (برای event listener ها)
  // ============================================================
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // ============================================================
// ============================================================
//  ۱. بارگذاری state از سرور هنگام تغییر کاربر
// ============================================================
useEffect(() => {
  let cancelled = false;
  const loadFromServer = async () => {
    // ریست ref ها هنگام تغییر کاربر
    rewardIdRef.current = null;
    hydratedUserRef.current = null;

    if (userId === 'guest') {
      dispatch({ type: "HYDRATE", coins: 0, xp: 0 });
      hydratedUserRef.current = 'guest';
      return;
    }

    const data = await fetchRewardsFromServer(userId);
    if (cancelled) return;

    rewardIdRef.current = data.id || null;
    dispatch({ type: "HYDRATE", coins: data.coins, xp: data.xp });
    hydratedUserRef.current = userId;
  };
  loadFromServer();
  return () => { cancelled = true; };
}, [userId]);

// ============================================================
//  ۲. ذخیره‌ی خودکار در سرور هنگام تغییر state
//     (فقط برای کاربر غیر-مهمان و بعد از hydrate شدن)
// ============================================================
useEffect(() => {
  if (userId === "guest") return;
  // ✅ اگر state هنوز برای این کاربر hydrate نشده، نوشتن را رد کن
  if (hydratedUserRef.current !== userId) return;
  saveRewardsToServer(userId, state.coins, state.xp, rewardIdRef.current);
}, [state.coins, state.xp, userId]);

  // ============================================================
  //  ۳. همگام‌سازی بین تب‌ها
// ============================================================
//  ۳. (حذف شد) همگام‌سازی بین تب‌ها از طریق localStorage
//     چون حالا سرور منبع اصلی است، این قابلیت لازم نیست.
//     هر تب هنگام بارگذاری، مقادیر تازه را از سرور می‌گیرد.
// ============================================================

  // ============================================================
  //  ۴. پاک‌سازی تایمر انیمیشن در unmount
  // ============================================================
  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, []);

  // ============================================================
  //  Helper: cooldown
  // ============================================================
  const canUse = useCallback(() => {
    const now = Date.now();
    if (now - lastActionTimeRef.current < COOLDOWN_MS) return false;
    lastActionTimeRef.current = now;
    return true;
  }, []);

  // ============================================================
  //  Helper: انیمیشن
  // ============================================================
  const triggerAnimation = useCallback((type, amount) => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
    setAnimation({
      id: Date.now() + Math.random(),
      type, // "add" | "remove"
      amount: toNonNegInt(amount),
      at: Date.now(),
    });
    animationTimerRef.current = setTimeout(() => {
      setAnimation(null);
      animationTimerRef.current = null;
    }, 1000);
  }, []);

  // ============================================================
  //  عملیات: افزودن سکه
  // ============================================================
  const addCoins = useCallback(
    (amount) => {
      const delta = toNonNegInt(amount);
      if (delta <= 0) return 0;
      if (userIdRef.current === "guest") return 0;
      if (!canUse()) return 0;

      dispatch({ type: "ADD_COINS", amount: delta });
      triggerAnimation("add", delta);
      return delta;
    },
    [canUse, triggerAnimation]
  );

  // ============================================================
  //  عملیات: افزودن XP
  // ============================================================
  const addXp = useCallback(
    (amount) => {
      const delta = toNonNegInt(amount);
      if (delta <= 0) return 0;
      if (userIdRef.current === "guest") return 0;
      if (!canUse()) return 0;

      dispatch({ type: "ADD_XP", amount: delta });
      triggerAnimation("add", delta);
      return delta;
    },
    [canUse, triggerAnimation]
  );

  // ============================================================
  //  عملیات: کاهش سکه
  // ============================================================
  const removeCoins = useCallback(
    (amount) => {
      const delta = toNonNegInt(amount);
      if (delta <= 0) return 0;
      if (userIdRef.current === "guest") return 0;
      if (!canUse()) return 0;

      dispatch({ type: "REMOVE_COINS", amount: delta });
      triggerAnimation("remove", delta);
      return delta;
    },
    [canUse, triggerAnimation]
  );

  // ============================================================
  //  عملیات: کاهش XP
  // ============================================================
  const removeXp = useCallback(
    (amount) => {
      const delta = toNonNegInt(amount);
      if (delta <= 0) return 0;
      if (userIdRef.current === "guest") return 0;
      if (!canUse()) return 0;

      dispatch({ type: "REMOVE_XP", amount: delta });
      triggerAnimation("remove", delta);
      return delta;
    },
    [canUse, triggerAnimation]
  );

  // ============================================================
  //  عملیات ترکیبی: افزودن همزمان سکه و XP (اتمی)
  // ============================================================
  const addRewards = useCallback(
    (coinAmount, xpAmount) => {
      const coinDelta = toNonNegInt(coinAmount);
      const xpDelta = toNonNegInt(xpAmount);

      if (coinDelta <= 0 && xpDelta <= 0) return { coins: 0, xp: 0 };
      if (userIdRef.current === "guest") return { coins: 0, xp: 0 };
      if (!canUse()) return { coins: 0, xp: 0 };

      // دو dispatch پشت سر هم — React آن‌ها را batch می‌کند
      if (coinDelta > 0) dispatch({ type: "ADD_COINS", amount: coinDelta });
      if (xpDelta > 0) dispatch({ type: "ADD_XP", amount: xpDelta });

      triggerAnimation("add", coinDelta + xpDelta);
      return { coins: coinDelta, xp: xpDelta };
    },
    [canUse, triggerAnimation]
  );

  // ============================================================
  //  خواندن موجودی هر کاربر (حتی کاربر دیگر)
  // ============================================================
const getUserRewards = useCallback(
  (uid) => {
    // ⚠️ برای کاربران دیگر، چون داده در حافظه نیست، صفر برمی‌گردانیم.
    //    (سرور منبع اصلی است — در صورت نیاز از getUserRewards در صفحات دیگر
    //     می‌توانید مستقیم از سرور query کنید.)
    if (uid && uid !== userIdRef.current) {
      return { coins: 0, xp: 0 };
    }
    return { coins: state.coins, xp: state.xp };
  },
  [state.coins, state.xp]
);

  // ============================================================
  //  جایگزینی کامل مقادیر (مثلاً از سرور)
  // ============================================================
  const setUserRewards = useCallback((newCoins, newXp) => {
    if (userIdRef.current === "guest") return;
    dispatch({ type: "SET", coins: newCoins, xp: newXp });
  }, []);

  // ============================================================
  //  صفر کردن مقادیر (برای logout)
  // ============================================================
  const clearUserRewards = useCallback(() => {
    if (userIdRef.current === "guest") return;
    dispatch({ type: "CLEAR" });
  }, []);

  // ============================================================
  //  Value نهایی Context (memoized)
  // ============================================================
  const value = useMemo(
    () => ({
      // ---- مقادیر ----
      coins: state.coins,
      xp: state.xp,

      // ---- عملیات اصلی ----
      addCoins,
      removeCoins,
      addXp,
      removeXp,
      addRewards,

      // ---- کمکی ----
      getUserRewards,
      setUserRewards,
      clearUserRewards,

      // ---- انیمیشن ----
      animation,

      // ---- اطلاعات محیطی ----
      isGuest: userId === "guest",
      userId,
    }),
    [
      state.coins,
      state.xp,
      addCoins,
      removeCoins,
      addXp,
      removeXp,
      addRewards,
      getUserRewards,
      setUserRewards,
      clearUserRewards,
      animation,
      userId,
    ]
  );

  return (
    <CoinContext.Provider value={value}>{children}</CoinContext.Provider>
  );
}

// ================================================================
//  Hook
// ================================================================
export function useCoins() {
  const ctx = useContext(CoinContext);
  if (!ctx) {
    throw new Error("useCoins must be used within a CoinProvider");
  }
  return ctx;
}